import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReviewsSection from './ReviewsSection';

const { 
  FiUser, FiCalendar, FiHome, FiMapPin, FiWifi, FiCoffee, 
  FiWind, FiSun, FiTv, FiDroplets, FiActivity, FiBriefcase, 
  FiHeart, FiCheck, FiX, FiClock, FiAlertTriangle, FiInfo,
  FiChevronDown, FiChevronUp, FiShield, FiStar
} = FiIcons;

const SectionDivider = () => <hr className="border-gray-100 my-8" />;

const HighlightItem = ({ icon, title, subtitle }) => (
  <div className="flex items-start gap-4 flex-1">
    <SafeIcon icon={icon} className="text-2xl text-gray-700 mt-1" />
    <div>
      <h4 className="font-bold text-gray-900 text-sm">{title}</h4>
      <p className="text-xs text-gray-500 leading-relaxed">{subtitle}</p>
    </div>
  </div>
);

const AmenityDetailCard = ({ title, items }) => (
  <div className="bg-white p-6 rounded-2xl border border-gray-100 border-l-4 border-l-emerald-600 shadow-sm">
    <h4 className="font-bold text-gray-900 mb-3 text-sm flex items-center gap-2">
      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      {title}
    </h4>
    <div className="flex flex-wrap gap-2">
      {items.map((item, idx) => (
        <span key={idx} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
          {item}
        </span>
      ))}
    </div>
  </div>
);

export default function ApartmentInfo({ t }) {
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  const mainAmenities = [
    { icon: FiWifi, label: "Wifi", available: true },
    { icon: FiCoffee, label: "Kitchen", available: true },
    { icon: FiWind, label: "Washer", available: true },
    { icon: FiSun, label: "Dryer", available: true },
    { icon: FiDroplets, label: "Pool", available: true },
    { icon: FiActivity, label: "Gym", available: true },
    { icon: FiTv, label: "TV", available: true },
    { icon: FiBriefcase, label: "Dedicated workspace", available: true },
    { icon: FiHeart, label: "Pets allowed", available: true },
    { icon: FiCalendar, label: "Long term stays allowed", available: true },
    { icon: FiCheck, label: "Self check-in", available: true },
    { icon: FiShield, label: "Building staff", available: true },
    { icon: FiX, label: "Air conditioning", available: false },
    { icon: FiAlertTriangle, label: "Smoke alarm", available: false },
  ];

  const amenityDetails = [
    { title: "Baño", items: ["productos de limpieza", "champú", "jabón corporal", "bidet", "gel de ducha"] },
    { title: "Dormitorio", items: ["lavadora", "secadora", "perchas", "ropa de cama", "almohadas extra", "cortinas blackout", "plancha", "armario"] },
    { title: "Entretenimiento", items: ["TV", "equipos de ejercicio"] },
    { title: "Internet", items: ["WiFi", "espacio de trabajo dedicado"] },
    { title: "Cocina", items: ["nevera", "microondas", "cocina", "horno", "cafetera", "licuadora", "lavavajillas", "copas de vino", "mesa de comedor", "café"] },
    { title: "Exterior", items: ["parrilla BBQ"] },
    { title: "Instalaciones", items: ["piscina", "ascensor", "gimnasio", "parqueadero (pago)"] },
    { title: "Servicios", items: ["mascotas permitidas", "estadías largas", "self check-in", "personal del edificio"] }
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 mt-12 mb-20 bg-white">
      {/* Header Section */}
      <section className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
          {t.entireRentalUnit}
        </h1>
        <div className="flex flex-wrap gap-2 text-sm text-gray-600 mb-4">
          <span>{t.guestsCount}</span> · <span>1 studio</span> · <span>2 beds</span> · <span>1 bath</span> · <span>Floor 8</span>
        </div>
        <div className="flex items-center gap-2 text-sm font-medium">
          <span className="flex items-center gap-1"><SafeIcon icon={FiStar} className="text-emerald-600" /> New</span>
          · <span>Reg. 278990</span> · <span>{t.hostedBy} John Javier</span> · <span>10 {t.yearsHosting}</span>
        </div>
      </section>

      <SectionDivider />

      {/* Host Card */}
      <section className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-emerald-600 rounded-full flex items-center justify-center text-white font-black text-xl border-4 border-emerald-50">
            JJ
          </div>
          <div>
            <h3 className="font-bold text-gray-900">{t.hostedBy} John Javier</h3>
            <p className="text-sm text-gray-500">{t.superhost} · 10 {t.yearsHosting}</p>
          </div>
        </div>
        <div className="text-right hidden sm:block">
          <p className="text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
            {t.selfCheckInAvailable}
          </p>
        </div>
      </section>

      <SectionDivider />

      {/* Highlights */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <HighlightItem 
          icon={FiDroplets} 
          title={t.diveRightIn} 
          subtitle={t.diveRightInDesc} 
        />
        <HighlightItem 
          icon={FiShield} 
          title={t.selfCheckInTitle} 
          subtitle={t.selfCheckInDesc} 
        />
        <HighlightItem 
          icon={FiMapPin} 
          title={t.greatLocationTitle} 
          subtitle={t.greatLocationDesc} 
        />
      </section>

      <SectionDivider />

      {/* About Section */}
      <section>
        <h3 className="text-xl font-bold text-gray-900 mb-4">{t.aboutTitle}</h3>
        <div className="relative">
          <div className={`text-gray-600 leading-relaxed text-sm ${!isAboutExpanded ? 'line-clamp-3' : ''}`}>
            {t.aboutText1}
            <br /><br />
            {t.aboutText2}
            <br /><br />
            {t.aboutText3}
          </div>
          <button 
            onClick={() => setIsAboutExpanded(!isAboutExpanded)}
            className="mt-4 text-gray-900 font-bold underline flex items-center gap-1 hover:text-emerald-700 transition-colors"
          >
            {isAboutExpanded ? t.showLess : t.showMore}
            <SafeIcon icon={isAboutExpanded ? FiChevronUp : FiChevronDown} />
          </button>
        </div>
      </section>

      <SectionDivider />

      {/* Amenities Grid */}
      <section>
        <h3 className="text-xl font-bold text-gray-900 mb-6">{t.offersTitle}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(showAllAmenities ? mainAmenities : mainAmenities.slice(0, 10)).map((amenity, idx) => (
            <div key={idx} className={`flex items-center gap-4 py-2 ${!amenity.available ? 'opacity-50 line-through' : ''}`}>
              <SafeIcon icon={amenity.icon} className="text-xl text-gray-700" />
              <span className="text-sm text-gray-700 font-medium">{amenity.label}</span>
            </div>
          ))}
        </div>
        <button 
          onClick={() => setShowAllAmenities(!showAllAmenities)}
          className="mt-8 px-6 py-3 border border-gray-900 rounded-xl font-bold text-sm hover:bg-gray-50 transition-colors"
        >
          {showAllAmenities ? t.showLessAmenities : t.showAllAmenities}
        </button>
      </section>

      <SectionDivider />

      {/* Amenities Detail Cards */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {amenityDetails.map((category, idx) => (
          <AmenityDetailCard key={idx} {...category} />
        ))}
      </section>

      <SectionDivider />

      {/* Reviews Section */}
      <ReviewsSection />

      <SectionDivider />

      {/* House Rules */}
      <section>
        <h3 className="text-xl font-bold text-gray-900 mb-6">{t.houseRulesTitle}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <span className="text-sm text-gray-700 font-medium">Check-in</span>
            <span className="text-sm font-bold text-gray-900">3:00 PM – 10:00 PM</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-50">
            <span className="text-sm text-gray-700 font-medium">Checkout</span>
            <span className="text-sm font-bold text-gray-900">11:00 AM</span>
          </div>
          {[
            "Estadía mínima: 2 noches",
            "No fumadores ni vapeo",
            "No fiestas ni eventos",
            "No visitas adicionales",
            "Documento físico obligatorio en recepción",
            "Solo huéspedes registrados previamente"
          ].map((rule, idx) => (
            <div key={idx} className="flex items-center gap-3 py-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="text-sm text-gray-700 font-medium">{rule}</span>
            </div>
          ))}
        </div>
      </section>

      <SectionDivider />

      {/* Cancellation Policy */}
      <section>
        <div className="bg-amber-50 rounded-3xl p-6 border border-amber-100 flex items-start gap-4">
          <div className="bg-amber-100 p-3 rounded-2xl">
            <SafeIcon icon={FiAlertTriangle} className="text-amber-700 text-2xl" />
          </div>
          <div>
            <h4 className="font-bold text-amber-900 mb-2">{t.cancellationTitle}</h4>
            <p className="text-amber-800 text-sm leading-relaxed font-medium">
              {t.cancellationText}
            </p>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Location Section */}
      <section>
        <h3 className="text-xl font-bold text-gray-900 mb-4">{t.locationTitle}</h3>
        <p className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-2">
          <SafeIcon icon={FiMapPin} className="text-emerald-700" /> Edificio Manzana Tres, Bogotá
        </p>
        <p className="text-xs text-gray-500 mb-6 leading-relaxed">
          {t.nearby}: Parque Simón Bolívar · Movistar Arena · El Campín · Estéreo Picnic · Chapinero · Centro Internacional
        </p>
        
        <div className="w-full h-[300px] rounded-xl overflow-hidden shadow-sm border border-gray-100">
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3976.6500241755343!2d-74.08121109080476!3d4.656347495298964!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8e3f9b2259766025%3A0x99bb724b162b8ddf!2sManzana3!5e0!3m2!1sen!2sbo!4v1777238283773!5m2!1sen!2sbo" 
            width="100%" 
            height="300" 
            style={{ border: 0, borderRadius: '12px' }} 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </div>
  );
}